import { useState, useEffect, useRef } from "react";
import { SidebarActions } from "@rapid/components/layout/SidebarActions";
import { SidebarHeader } from "@rapid/components/layout/SidebarHeader";
import { PromptLabelList } from "@rapid/components/flow/build/Steps/Prompt/SidebarContent/PromptLabel/PromptLabelList";
import { GeneratePredictions } from "@rapid/components/flow/build/Steps/Prompt/SidebarContent/Annotate/GeneratePredictions";
import { ThresholdTracker } from "@rapid/components/flow/shared/Deploy/ThresholdTracker";
import { DEFAULT_CHILD_ANIMATION_DELAY, DEFAULT_DURATION } from "@rapid/constants/motion";
import { BuildPromptSteps } from "@rapid/types/flows";
import { useApprovedCount, useCurrentRapidData, useLimitedSources } from "@rapid/store";
import { useDatasetImageCount } from "@rapid/hooks/useDatasetImageCount";
import { useFlushCITrainingJob } from "@rapid/hooks/useFlushCITrainingJob";
import { getNextThreshold } from "@rapid/utils/getNextThreshold";
import { CIEvaluationResult } from "@functions/continuousImprovement/continuousImprovementTypes";
import { ApproveImageBtn } from "./ApproveImageBtn";

export function ImproveSidebarContent() {
    const approvedCount = useApprovedCount();
    const limitedSources = useLimitedSources();
    const currentRapidData = useCurrentRapidData();
    const { count: datasetImageCount, datasetId } = useDatasetImageCount();

    // Capture the dataset image count and its next threshold on mount so we can
    // optimistically project the count as approvals happen (Firestore has
    // a round-trip delay before the `images` field updates).
    const initialDatasetImageCount = useRef<number | null>(null);
    const initialNextThreshold = useRef<number | null>(null);
    if (datasetImageCount !== null && initialDatasetImageCount.current === null) {
        initialDatasetImageCount.current = datasetImageCount;
        initialNextThreshold.current = getNextThreshold(datasetImageCount);
    }

    const optimisticImageCount =
        datasetImageCount !== null && initialDatasetImageCount.current !== null
            ? Math.max(datasetImageCount, initialDatasetImageCount.current + approvedCount)
            : datasetImageCount;

    const hasScheduledJob =
        currentRapidData?.lastCIEvaluation?.result === CIEvaluationResult.TRAINING_SCHEDULED;
    const crossedThreshold =
        initialNextThreshold.current !== null &&
        optimisticImageCount !== null &&
        optimisticImageCount >= initialNextThreshold.current;

    const { flush: handleTrainNowClick } = useFlushCITrainingJob(datasetId, {
        force: crossedThreshold
    });

    const [showCTAs, setShowCTAs] = useState(false);

    // Show CTAs after parent animation completes
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowCTAs(true);
        }, DEFAULT_CHILD_ANIMATION_DELAY);

        return () => clearTimeout(timer);
    }, []);

    return (
        <>
            <SidebarHeader
                title="How do the objects look?"
                description="Review and edit the boxes until they look good for you."
                showImageCountChip
                currentImageCount={approvedCount}
                totalImagesNeeded={limitedSources.length}
            />
            <div className="content">
                <GeneratePredictions />
                <PromptLabelList mode={BuildPromptSteps.ANNOTATE_DATA} />
            </div>
            <SidebarActions
                toolbarProps={{
                    initial: { opacity: 0 },
                    animate: { opacity: showCTAs ? 1 : 0 },
                    transition: { duration: DEFAULT_DURATION.slower, ease: "easeOut" }
                }}
            >
                {optimisticImageCount !== null && (
                    <div className="mb-3">
                        <ThresholdTracker
                            currentImages={optimisticImageCount}
                            highlight={approvedCount > 0}
                            showTrainNow={hasScheduledJob || crossedThreshold}
                            onTrainNowClick={handleTrainNowClick}
                        />
                    </div>
                )}
                <ApproveImageBtn
                    hasScheduledJob={hasScheduledJob || crossedThreshold}
                    onRetrain={handleTrainNowClick}
                />
            </SidebarActions>
        </>
    );
}
